import React, { useContext, useRef, useState } from "react";
import axios from "axios";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetMyUploadsCall } from "../../redux/api/uploads.api";
import { basicURL } from "../../apiManager/APIConfig";
import { store } from "../../redux/store";

const ImageElement = (props: ICanvasComponent) => {
  const { content, id, isBackground, isPlayMode } = props;
  const { actions, uploadEntity, uploadsOnlyFromLibrary } = useContext(CanvasContext);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerItems, setPickerItems] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  const getBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const getImageDimensions = async (
    file: string
  ): Promise<{ [key: string]: number }> => {
    return new Promise((resolved) => {
      const i = new Image();
      i.onload = function () {
        resolved({
          w: i.width,
          h: i.height,
          nw: i.naturalWidth,
          nh: i.naturalHeight
        });
      };
      i.src = file;
    });
  };

  const getAdjustedDimensions = (
    width: number,
    height: number,
    resultWidth: number
  ) => {
    const ratio = width / height;
    return { calcWidth: resultWidth, calcHeight: resultWidth / ratio };
  };

  const uploadToBackend = async (file: File): Promise<string | null> => {
    if (!uploadEntity) return null;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("entity_type", uploadEntity.entity_type);
    formData.append("entity_id", uploadEntity.entity_id);
    formData.append("destination", "presentation");
    formData.append("folder_name", "presentation");
    const token = store.getState().auth?.access_token;
    const res = await axios.post(`${basicURL}file/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const url = res?.data?.download_url;
    return url || null;
  };

  const imageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
    if (!isImage) return;
    setUploading(true);
    try {
      let imageUrl: string;
      if (uploadEntity) {
        const url = await uploadToBackend(file);
        if (!url) throw new Error("Upload failed");
        imageUrl = url;
      } else {
        imageUrl = (await getBase64(file)) as string;
      }
      const imageDimensions = await getImageDimensions(imageUrl);
      const { calcWidth, calcHeight } = getAdjustedDimensions(
        imageDimensions?.nw,
        imageDimensions?.nh,
        150
      );
      actions?.updateCanvasData({
        id,
        content: imageUrl,
        dimension: {
          width: `${calcWidth || 0}`,
          height: `${calcHeight || 0}`
        }
      });
      actions?.saveNow?.();
    } catch (err) {
      console.error("Image upload error:", err);
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const triggerUpload = () => {
    const element = uploadRef?.current;
    if (element) {
      element.click();
    }
  };

  const renderUploadContent = () => {
    return (
      <>
        {!uploadsOnlyFromLibrary && (
          <>
            <div
              className="image-upload-container"
              onClick={() => !uploading && triggerUpload()}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f0f0f0",
                borderRadius: 4,
                cursor: uploading ? "wait" : "pointer",
              }}
            >
              <div>{uploading ? "Uploading…" : "Upload Image"}</div>
            </div>
            <input
              ref={uploadRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              onChange={imageUpload}
              style={{ display: "none" }}
            />
          </>
        )}
        {uploadsOnlyFromLibrary && (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f0f0f0",
              borderRadius: 4,
              fontSize: 12,
              color: "#64748b",
              textAlign: "center",
              padding: 8,
            }}
          >
            Use the Uploads panel to add images
          </div>
        )}
        <button
          type="button"
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            border: "1px solid #cbd5e1",
            background: "#fff",
            cursor: "pointer",
          }}
          onClick={async () => {
            try {
              setShowPicker(true);
              setPickerLoading(true);
              const items = await GetMyUploadsCall();
              setPickerItems(items.filter((f: any) => f.type?.startsWith("image/")));
            } catch (e) {
              console.error("Failed to load uploads for picker", e);
            } finally {
              setPickerLoading(false);
            }
          }}
        >
          Pick from uploads
        </button>
      </>
    );
  };

  const renderImage = () => {
    return (
      <div
        style={{
          backgroundImage: `url(${content})`,
          backgroundSize: isBackground ? "cover" : "contain",
          backgroundPosition: "center",
          width: "100%",
          height: "100%",
          backgroundRepeat: "no-repeat"
        }}
      />
    );
  };

  if (isPlayMode) {
    // In fullscreen/play mode we only show the image itself (or a neutral background),
    // never the upload UI.
    if (!content) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#e5e7eb",
            borderRadius: 4,
          }}
        />
      );
    }
    return renderImage();
  }

  return (
    <>
      {!content ? renderUploadContent() : renderImage()}
      {showPicker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
          onClick={() => setShowPicker(false)}
        >
          <div
            style={{
              width: 640,
              maxWidth: "90vw",
              maxHeight: "80vh",
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 24px 60px rgba(15,23,42,0.38)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>My uploads</span>
              <button
                type="button"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 18,
                }}
                onClick={() => setShowPicker(false)}
              >
                ×
              </button>
            </div>
            {pickerLoading ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                Loading…
              </div>
            ) : pickerItems.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                No uploads yet. Use the Uploads page to add private images.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 10,
                  overflowY: "auto",
                }}
              >
                {pickerItems.map((file: any) => (
                  <button
                    key={file.id || file.url}
                    type="button"
                    onClick={() => {
                      actions?.updateCanvasData({
                        id,
                        content: file.url,
                      });
                      actions?.saveNow?.();
                      setShowPicker(false);
                    }}
                    style={{
                      border: "none",
                      padding: 0,
                      borderRadius: 12,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: 100,
                        backgroundImage: `url(${file.url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageElement;
